<template>
  <div
    :class="{
      'grid grid-cols-12 gap-6': true,
    }"
  >
    <div
      v-for="(parent, parentIndex) in taxonomyOptionsConfig"
      :key="parent.id"
      :class="{
        'col-span-12 mb-4 h-fit bg-www-color-white md:col-span-6 lg:col-span-12': true,
      }"
    >
      <label
        :class="{
          'relative block w-full cursor-pointer bg-www-color-primary-lighter py-10px pl-10px pr-9 text-sm font-black uppercase leading-none text-www-color-white transition-all duration-300 ease-in-out': true,
          'after:absolute after:right-5 after:top-1/2 after:-translate-y-1/2 after:border-b-[2px] after:border-r-[2px] after:p-[2px] after:content-[\'\']': true,
          'after:-rotate-[135deg]': showList[parent.id],
          'after:rotate-45': !showList[parent.id],
        }"
        @click="toggleFeatures(parent.id)"
        >{{ parent.label }}</label
      >

      <div>
        <Transition name="slide">
          <div
            v-if="showList[parent.id]"
            :ref="(el) => setCollapsableList(el as HTMLElement, parentIndex)"
            :ref-id="parent.id"
            :class="{
              'parent overflow-y-auto': true,
            }"
          >
            <div
              v-if="parent.slug === 'taxonomy-product-aspects'"
              :class="{
                'flex items-center gap-10px bg-www-color-neutral-very-light p-10px': true,
              }"
            >
              <input
                type="text"
                placeholder="Search"
                :class="{
                  'h-30px flex-grow border border-www-color-neutral-light px-10px text-xs placeholder:text-xs': true,
                }"
                @keyup="filterTaxonomyList"
              />
              <PtcIcon
                name="ptc-search"
                :class="{
                  'h-5 w-5 text-www-color-neutral-medium': true,
                }"
              />
            </div>
            <div
              v-for="(child, childIndex) in parent.children"
              :key="child.id"
              :class="{
                'filter-item': true,
              }"
              data-component-name="filterItem"
            >
              <div v-if="child.children.length > 0">
                <div
                  :class="{
                    'relative flex cursor-pointer items-center gap-10px border-b border-b-[#e0e0e0] p-10px': true,
                    'after:absolute after:right-5 after:top-1/2 after:-translate-y-1/2 after:border-b-[2px] after:border-r-[2px] after:p-[2px] after:content-[\'\']': true,
                    'after:-rotate-[135deg]': showList[child.id],
                    'after:rotate-45': !showList[child.id],
                  }"
                  @click="toggleFeatures(child.id)"
                >
                  <label
                    :class="{
                      'text-xs font-medium uppercase leading-5 text-www-color-accent-teal': true,
                    }"
                  >
                    {{ child.label }}
                  </label>
                </div>
                <Transition name="slide">
                  <div
                    v-if="showList[child.id]"
                    :ref="
                      (el) =>
                        setCollapsableList(
                          el as HTMLElement,
                          childIndex + taxonomyOptionsConfig.length + 1
                        )
                    "
                    :ref-id="child.id"
                    :class="{
                      'grandchild transition-all duration-500 ease-in-out': true,
                    }"
                  >
                    <div
                      v-for="grandChild in child.children"
                      :key="grandChild.id"
                      :class="{
                        'grandChild cursor-pointer items-center gap-10px border-b border-b-[#e0e0e0] p-10px': true,
                        hidden:
                          searchTerm && grandChild.label.toLowerCase().indexOf(searchTerm) === -1,
                        flex:
                          searchTerm === '' ||
                          grandChild.label.toLowerCase().indexOf(searchTerm) !== -1,
                      }"
                    >
                      <input
                        :id="`sidebar-filter-${grandChild.id}`"
                        ref="checkboxes"
                        :checked="taxonomyCheckedStates[grandChild.slug]"
                        :data-id="grandChild.id"
                        :class="{
                          'check h-5 w-5 cursor-pointer rounded-none border border-www-color-neutral-very-light accent-www-color-primary-lighter': true,
                        }"
                        :value="grandChild.slug"
                        type="checkbox"
                        @change="
                          emitChange({
                            id: grandChild.id,
                            isChecked: !taxonomyCheckedStates[grandChild.slug],
                            slug: grandChild.slug,
                          })
                        "
                      />
                      <label
                        :for="`sidebar-filter-${grandChild.id}`"
                        :class="{
                          'text-xs leading-5 text-www-color-neutral-medium-dark': true,
                        }"
                      >
                        {{ grandChild.label }}
                      </label>
                    </div>
                  </div>
                </Transition>
              </div>
              <div
                v-else-if="child.children.length === 0"
                :class="{
                  'flex cursor-pointer items-center gap-10px border-b border-b-[#e0e0e0] p-10px': true,
                }"
              >
                <input
                  :id="`sidebar-filter-${child.id}`"
                  ref="checkboxes"
                  :checked="taxonomyCheckedStates[child.slug]"
                  :data-id="child.id"
                  :class="{
                    'check h-5 w-5 cursor-pointer rounded-none border border-www-color-neutral-very-light accent-www-color-primary-lighter': true,
                  }"
                  :value="child.slug"
                  type="checkbox"
                  @change="
                    emitChange({
                      id: child.id,
                      isChecked: !taxonomyCheckedStates[child.slug],
                      slug: child.slug,
                    })
                  "
                />
                <label
                  :for="`sidebar-filter-${child.id}`"
                  :class="{
                    'text-xs leading-5 text-www-color-neutral-medium-dark': true,
                  }"
                >
                  {{ child.label }}
                </label>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PtcIcon from '~/components/Atmos/PtcIcon/PtcIcon.vue';
import type { TaxonomyChangedPayload } from '~/composables/useContentFilter';
const props = defineProps({
  taxonomyOptionsConfig: {
    type: Object as PropType<TaxonomyFilterOptionsConfig>,
    required: true,
  },
  taxonomyCheckedStates: {
    type: Object as PropType<TaxonomyCheckedStates>,
    required: true,
  },
  loaded: {
    type: Boolean,
    default: false,
  },
});
const { taxonomyOptionsConfig, taxonomyCheckedStates, loaded } = toRefs(props);
const showList = ref<Record<string, boolean>>({});
const collapsableList = ref<HTMLElement[]>([]);
const searchTerm = ref<string>('');
// Function to set the collapsable list elements in the array
function setCollapsableList(el: HTMLElement | null, index: number) {
  if (el) {
    collapsableList.value[index] = el;
  }
}

// Set Max Height for each collapsable item based on it is opened or closed and on searching also
watch(
  () => [searchTerm.value, loaded.value, JSON.stringify(showList.value)],
  async () => {
    if (!loaded.value) return;
    await nextTick(); // Wait until DOM updates after filtering
    collapsableList.value.forEach((collapsableItem) => {
      if (!collapsableItem) return;
      const refId = collapsableItem.getAttribute('ref-id');
      if (!refId) return;
      const isOpened = showList.value[refId];
      if (isOpened) {
        // Opening — set to scrollHeight
        collapsableItem.style.transition = 'max-height 0.5s ease';
        collapsableItem.style.maxHeight = `${collapsableItem.scrollHeight}px`;
      } else {
        // Closing — set to current height first, then to 41px
        collapsableItem.style.transition = 'none'; // Disable transition first
        const currentHeight = collapsableItem.scrollHeight;

        collapsableItem.style.maxHeight = `${currentHeight}px`;

        // Force a reflow to apply this maxHeight before transition
        void collapsableItem.offsetHeight;

        // Now enable transition and set to collapsed height
        collapsableItem.style.transition = 'max-height 0.5s ease';
        collapsableItem.style.maxHeight = '41px';
      }
    });
  }
);

const checkboxes = ref<Array<HTMLInputElement>>([]);

const emit = defineEmits<{
  (_evt: 'taxonomyChange', _payload: TaxonomyChangedPayload): void;
}>();

const emitChange = (payload: TaxonomyChangedPayload) => {
  emit('taxonomyChange', payload);
};

const toggleFeatures = (parentId: string) => {
  showList.value[parentId] = !showList.value[parentId];
};
const filterTaxonomyList = (event: KeyboardEvent) => {
  const input = event.target as HTMLInputElement;
  const filterValue = input.value.toLowerCase();
  searchTerm.value = filterValue;
};
// handle the case were the dom has the checkboxes from SSG, but we don't have
// have the vue code ready to listen for changes
onMounted(() => {
  if (!checkboxes.value) {
    console.warn('No checkboxContainer found');
    return;
  }
  checkboxes.value
    .filter((checkbox) => checkbox.checked)
    .forEach((checkbox) => {
      if (checkbox.dataset.slug === undefined) {
        console.warn('Checkbox slug is undefined', checkbox.id);
        return;
      }
      emitChange({ slug: checkbox.value, isChecked: true, id: checkbox.dataset.id! });
    });

  // initialize the open state of all items to true
  taxonomyOptionsConfig.value.forEach((parent) => {
    showList.value[parent.id] = true; // Set all parent items to open by default
    parent.children.forEach((child) => {
      showList.value[child.id] = true;
      child.children.forEach((grandChild) => {
        showList.value[grandChild.id] = true;
      });
    });
  });
});
</script>

<style>
.slide-enter-to,
.slide-leave-from {
  overflow: hidden;
}
</style>
